import { MoneyText } from '@/components/MoneyText';
import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { SuccessIcon } from '@/components/icons';
import { useTranslation } from 'react-i18next';
import { allocationsApi, type AllocationLineRequest, type BulkItemError } from '@/api/allocations';
import { ErrorCodes, isApiError } from '@/api/errors';
import type { OwnerRegisterLine } from '@/api/transactions';
import { ErrorState } from '@/components/ErrorState';
import { formatCivilDate, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { generateRequestId } from '@/utils/requestId';
import { applyBackendFields, validationAlert } from '@/features/shared/useFieldErrors';
import { resolveTokens } from '@/theme/theme';
import { allocationErrorMessage, bulkItemErrorMessage } from './allocationErrors';
import {
  newRow,
  parsePercentBp,
  previewAmounts,
  toLineRequest,
  type AllocateRow,
  type ParsedSlice,
} from './allocateRows';
import { DestinationGroupList, useDestinationGroups } from './AllocateDestinations';
import { SubmitButton } from '@/components/SubmitButton';
import { GuardedDialog } from '@/components/GuardedDialog';
import { useReturnFocus } from '@/components/useConfirm';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { VirtualAccountRequiredPrompt } from '@/features/virtualAccounts/VirtualAccountRequiredPrompt';
import { useOptionalVirtualAccountsList } from '@/features/virtualAccounts/virtualAccountsListContext';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';

const TITLE_ID = 'bulk-allocate-title';
const SUBSCRIBE_TITLE_ID = 'bulk-allocate-subscribe-title';
const SUBMIT_OPENER = 'bulk-allocate-submit';

interface BulkAllocateDialogProps {
  lines: OwnerRegisterLine[];
  operatingCurrency: string | null;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}

/**
 * Bulk allocate (TMMAPP-0008; onto the allocate dialog's destinations by TMMAPP-0110, the 0109 review):
 * the one destinations UI (`DestinationGroupList`) — Unassigned first, every envelope, a category per
 * row, Amount / % / Rest, the groups the caller owns or manages — applied to every selected line in
 * one `POST /v1/allocations/bulk`. Each line takes the same rows; an amount carries the line's own
 * sign. The previews are the single dialog's one rule, per line, summed across the selection; the
 * server resolves each line and reports per item.
 */
export function BulkAllocateDialog({
  lines,
  operatingCurrency,
  onClose,
  onDone,
}: BulkAllocateDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const {
    groups,
    virtualAccounts,
    categoriesOf,
    status: destinationsStatus,
    error: destinationsError,
    reload: reloadDestinations,
  } = useDestinationGroups();
  // One row per destination, seeded once the destinations are known.
  const [rows, setRows] = useState<AllocateRow[] | null>(null);
  const seedKey = groups
    .map((group) => group.destinations.map((d) => d.envelopeId ?? 'u').join('/'))
    .join('|');
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (destinationsStatus === 'ready' && seededFor !== seedKey) {
    setSeededFor(seedKey);
    setRows(
      groups.flatMap((group) =>
        group.destinations.map((d) => newRow(d.virtualAccountId, d.envelopeId)),
      ),
    );
  }
  const current = rows ?? [];
  const [results, setResults] = useState<
    { transactionId: number; status: string; error: BulkItemError | null }[] | null
  >(null);
  const [submitting, setSubmitting] = useState(false);
  // SUBSCRIPTION_REQUIRED shows the subscribe card in place of the sheet (TMMAPP-0097 §C 25).
  const [subscribeRequired, setSubscribeRequired] = useState(false);
  // Back from the subscribe card, focus returns to "Allocate all", which asked for it.
  useReturnFocus(subscribeRequired, SUBMIT_OPENER);
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const idempotencyKey = useRef<string | null>(null);
  // Once the bulk save has landed the dialog only shows its results: nothing is left to lose.
  // Unsaved work: any row touched (the seeded rows are empty amounts, uncategorized).
  const dirty = current.some(
    (row) => row.value.trim().length > 0 || row.mode !== 'amount' || row.categoryId !== '',
  );
  const guard = useDialogDirtyGuard(dirty && results === null, onClose, submitting);
  // No virtual account yet (TMMAPP-0116): the destinations give way to the create prompt.
  const openCreateVirtualAccount = useOptionalVirtualAccountsList()?.openCreate;

  // A row's typed slice for one line: an amount carries that line's sign.
  const parseFor = (row: AllocateRow, sign: number): ParsedSlice => {
    if (row.mode === 'rest') return { kind: 'rest' };
    if (row.value.trim().length === 0) return null;
    if (row.mode === 'percent') {
      const percentBp = parsePercentBp(row.value);
      return percentBp === null ? null : { kind: 'percent', percentBp };
    }
    const magnitude = parseMoneyToMinor(row.value, operatingCurrency, locale);
    return magnitude === null || magnitude === 0
      ? null
      : { kind: 'amount', amountMinor: sign * Math.abs(magnitude) };
  };
  const signOf = (line: OwnerRegisterLine) => (line.operatingAmountMinor < 0 ? -1 : 1);
  const invalidTyped = current.some(
    (row) => row.mode !== 'rest' && row.value.trim().length > 0 && parseFor(row, 1) === null,
  );
  const restCount = current.filter((row) => row.mode === 'rest').length;
  // The single dialog's preview rule, per line (its operating amount, its parts already published),
  // summed across the selection.
  const shown = current.map(() => null as number | null);
  for (const line of lines) {
    const parsed = current.map((row) => parseFor(row, signOf(line)));
    const preview = previewAmounts(
      current,
      parsed,
      line.operatingAmountMinor,
      line.operatingAmountMinor - line.remainderMinor,
    );
    preview.forEach((amount, i) => {
      if (amount !== null) shown[i] = (shown[i] ?? 0) + amount;
    });
  }
  const filled = current.filter((row) => parseFor(row, 1) !== null);

  function setRow(key: string, patch: Partial<AllocateRow>) {
    if (error !== null) idempotencyKey.current = null;
    setRows((prev) => (prev ?? []).map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  async function handleSubmit() {
    if (filled.length === 0 || invalidTyped || restCount > 1 || submitting || results !== null) {
      return;
    }
    if (idempotencyKey.current === null) idempotencyKey.current = generateRequestId();
    const key = idempotencyKey.current;
    const items = lines
      .map((line) => ({
        transactionId: line.id,
        lines: filled.flatMap((row): AllocationLineRequest[] => {
          const slice = parseFor(row, signOf(line));
          return slice === null ? [] : [toLineRequest(row, slice)];
        }),
      }))
      .filter((item) => item.lines.length > 0);
    setSubmitting(true);
    setError(null);
    setRowErrors({});
    try {
      const result = await allocationsApi.bulk({ items }, key);
      await onDone();
      setResults(result.items);
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        setSubscribeRequired(true);
      } else {
        if (isApiError(err) && err.code === ErrorCodes.IDEMPOTENCY_CONFLICT) {
          idempotencyKey.current = generateRequestId();
        }
        if (isApiError(err) && err.code === ErrorCodes.VALIDATION_FAILED) {
          const next: Record<string, string> = {};
          const known = items.flatMap((_item, n) =>
            filled.flatMap((_row, i) =>
              ['amountMinor', 'envelopeId', 'virtualAccountId', 'categoryId'].map(
                (field) => `items[${n}].lines[${i}].${field}`,
              ),
            ),
          );
          const result = applyBackendFields(
            err,
            known,
            (field, message) => {
              const match = /^items\[\d+\]\.lines\[(\d+)\]/.exec(field);
              const row = match === null ? undefined : filled[Number(match[1])];
              if (row !== undefined) next[row.key] = message;
            },
            t,
            guard.focusErrors,
          );
          setRowErrors(next);
          setError(validationAlert(result, err, t, t('accounts.errorValidation')));
        } else if (isApiError(err) && err.code === ErrorCodes.ALLOCATION_CURRENCY_MISMATCH) {
          // A contribution goes in the owner's currency only (MM-D-120): the message sits on the rows
          // bound for a virtual account in another currency (every filled row if none is).
          const foreign = filled.filter((row) => {
            const currency =
              virtualAccounts.find((entry) => entry.id === row.virtualAccountId)?.currency ?? '';
            return currency.length > 0 && currency !== operatingCurrency;
          });
          const message = t('allocations.errorCurrencyMismatch');
          setRowErrors(
            Object.fromEntries(
              (foreign.length > 0 ? foreign : filled).map((r) => [r.key, message]),
            ),
          );
          guard.focusErrors();
        } else {
          setError(
            allocationErrorMessage(err, t, operatingCurrency, locale) ?? t('accounts.errorGeneric'),
          );
          guard.focusErrors();
        }
      }
    } finally {
      setSubmitting(false);
    }
  }

  // The subscribe card in place of the sheet, in this same dialog; Esc and Cancel go back to it.
  if (subscribeRequired) {
    return (
      <GuardedDialog
        guard={guard}
        confirm={{ titleId: SUBSCRIBE_TITLE_ID, onClose: () => setSubscribeRequired(false) }}
        fullWidth
        maxWidth="md"
      >
        <DialogContent>
          <SubscribeInPlace titleId={SUBSCRIBE_TITLE_ID} focusTitle onClose={onClose} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscribeRequired(false)}>{t('common.cancel')}</Button>
        </DialogActions>
      </GuardedDialog>
    );
  }

  return (
    <GuardedDialog guard={guard} titleId={TITLE_ID} fullWidth maxWidth="md">
      <DialogTitle id={TITLE_ID}>{t('accounts.bulkTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          <Stack spacing={0.5}>
            {lines.map((line) => (
              <Typography key={line.id} variant="body2">
                {formatCivilDate(line.postedOn, locale)} · {line.payee} ·{' '}
                <MoneyText minor={line.remainderMinor} currency={operatingCurrency} />
              </Typography>
            ))}
          </Stack>
          <Typography variant="overline" component="h3">
            {t('allocate.destinations')}
          </Typography>
          {destinationsStatus === 'error' && (
            <ErrorState
              variant="section"
              error={destinationsError}
              what={t('errors.what.splitDestinations')}
              onRetry={() => void reloadDestinations()}
            />
          )}
          {destinationsStatus === 'ready' && groups.length === 0 && (
            <VirtualAccountRequiredPrompt
              onCreate={
                openCreateVirtualAccount !== undefined
                  ? () => {
                      onClose();
                      openCreateVirtualAccount();
                    }
                  : undefined
              }
            />
          )}
          {rows !== null && (
            <Box>
              <DestinationGroupList
                groups={groups}
                rows={current}
                shown={shown}
                resolved={null}
                rowErrors={rowErrors}
                currency={operatingCurrency}
                categoriesOf={categoriesOf}
                onChange={setRow}
                onRemove={(rowKey) =>
                  setRows((prev) => (prev ?? []).filter((row) => row.key !== rowKey))
                }
                onAdd={(group) =>
                  setRows((prev) => [...(prev ?? []), newRow(group.virtualAccount.id, null)])
                }
                onFocusAmount={() => undefined}
              />
            </Box>
          )}
          {restCount > 1 && (
            <Typography variant="body2" sx={{ color: (theme) => resolveTokens(theme).errorText }}>
              {t('allocate.oneRest')}
            </Typography>
          )}
          {results !== null && (
            <Stack spacing={0.5}>
              {results.map((item) => (
                <Stack
                  key={item.transactionId}
                  direction="row"
                  sx={{ gap: 1, alignItems: 'center' }}
                >
                  {item.error === null ? (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        color: (theme) => resolveTokens(theme).successFg,
                      }}
                    >
                      <SuccessIcon size={20} />
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ color: (theme) => resolveTokens(theme).errorText }}
                    >
                      {bulkItemErrorMessage(item.error, t, operatingCurrency, locale)}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {lines.find((l) => l.id === item.transactionId)?.payee ?? item.transactionId}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={guard.requestClose}>{t('common.close')}</Button>
        <SubmitButton
          data-confirm-opener={SUBMIT_OPENER}
          onClick={() => void handleSubmit()}
          disabled={filled.length === 0 || invalidTyped || restCount > 1 || results !== null}
          loading={submitting}
        >
          {t('accounts.bulkSubmit')}
        </SubmitButton>
      </DialogActions>
    </GuardedDialog>
  );
}
